import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Platform, StyleSheet } from 'react-native'
import type { TextStyle, ViewStyle } from 'react-native'
import { useTheme } from 'styled-components/native'
import { ImageMapSelector } from '@dialogue/components'
import type {
  ChoiceOption,
  Coordinates,
  ListMetadata,
  PromptChoiceImageMapPropsComplete,
} from 'APP/Services/Mattermost/types'

// Components
import { PrimaryButton, SecondaryButton } from 'APP/Components/Buttons'
import Tabs, { TabContent } from 'APP/Components/Tabs'
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import GroupedChoicePicker from 'APP/Components/GroupedChoicePicker'
import ZoomHint from 'APP/Components/ZoomHint'

// Services
import I18n from 'APP/I18n'
import { isWeb } from 'APP/Helpers/checkPlatform'

// Styles
import { Images } from 'APP/Themes'
import {
  ActionButtonContainer,
  Footer,
  HintContainer,
  HintContent,
  HintIcon,
  HintIconButton,
  PopoverContent,
  PopoverSubtitle,
  PopoverTail,
  PopoverWindow,
  getFooterSpacingStyle,
  getImagePickerStyle,
  getNoticeTitleStyle,
  getNoticeStyle,
  getPickerContainerStyle,
} from './style'

interface TabsItemProps {
  title?: string | null
  icon?: string | null
  testID?: string
}

interface TabContentItemProps {
  children: React.ReactNode
  index: number
}

const TabsItem = Tabs.Item as React.ComponentType<TabsItemProps>
const TabContentItem = TabContent.Item as React.ComponentType<TabContentItemProps>

interface GroupedChoicePickerProps {
  multi?: boolean
  groups?: unknown[]
  selectedChoices?: string[]
  onChoicePress: (choice: ChoiceOption) => void
  analyticsName?: string
  testID?: string
  style?: ViewStyle
}

const GroupedChoicePickerComponent =
  GroupedChoicePicker as React.ComponentType<GroupedChoicePickerProps>

interface ImageMapHintProps {
  maxSelectionCount?: number | null
}

const ImageMapHint: React.FC<ImageMapHintProps> = ({ maxSelectionCount }) => {
  const [showHint, setShowHint] = useState(false)

  const toggleOpen = () => {
    setShowHint(!showHint)
  }

  const hintDescription = `${
    (maxSelectionCount ?? 0) > 0
      ? I18n.t('ImageMap.hint.descriptionSelectionLimit', { maxSelectionCount })
      : I18n.t('ImageMap.hint.descriptionNoSelectionLimit')
  } ${I18n.t('ImageMap.hint.description')}`

  return (
    <HintContainer>
      <HintContent>
        <HintIconButton onPress={toggleOpen} analyticsName="Image map more info">
          <HintIcon source={Images.questionMarkCircle} resizeMode="contain" />
        </HintIconButton>
        {showHint && (
          <PopoverWindow>
            <PopoverTail />
            <PopoverContent>
              <PopoverSubtitle children={hintDescription} />
            </PopoverContent>
          </PopoverWindow>
        )}
      </HintContent>
    </HintContainer>
  )
}

interface ActionButtonsProps {
  onReset: () => void
  onNextPress: () => void
  submitDisabled: boolean
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ onReset, onNextPress, submitDisabled }) => {
  return (
    <Footer>
      <ActionButtonContainer>
        <SecondaryButton onPress={onReset} analyticsName="IntakeComponent.reset" testID="RESET_BTN">
          {I18n.t('IntakeComponent.reset')}
        </SecondaryButton>
      </ActionButtonContainer>
      <ActionButtonContainer>
        <PrimaryButton
          analyticsName="IntakeComponent.next"
          disabled={submitDisabled}
          onPress={onNextPress}
          testID="NEXT_BTN"
        >
          {I18n.t('IntakeComponent.next')}
        </PrimaryButton>
      </ActionButtonContainer>
    </Footer>
  )
}

interface DenormalizedListMetadata extends Omit<ListMetadata, 'children' | 'choices'> {
  children?: DenormalizedListMetadata[] | null
  choices?: ChoiceOption[] | null
}

const denormalizeListMetadata = (
  group: ListMetadata[] | null | undefined,
  choices: Record<string, ChoiceOption>
): DenormalizedListMetadata[] | undefined =>
  group?.map((child) => {
    if (child.children) {
      return {
        ...child,
        children: denormalizeListMetadata(child.children, choices),
      } as DenormalizedListMetadata
    } else if (child.choices) {
      return {
        ...child,
        choices: child.choices.map((id) => choices[id]),
      } as DenormalizedListMetadata
    }
    return child as DenormalizedListMetadata
  })

interface ImageMapPickerProps
  extends Pick<
    PromptChoiceImageMapPropsComplete,
    | 'map_title'
    | 'map_icon'
    | 'list_title'
    | 'list_icon'
    | 'list_metadata'
    | 'image_source'
    | 'image_metadata'
    | 'choices'
    | 'minimum_selection'
    | 'maximum_selection'
  > {
  disabled?: boolean
  sendAnswer: (
    selectedChoices: ChoiceOption[],
    selectedMetadata: Record<string, Coordinates>
  ) => void
  ready?: boolean
  includeListView?: boolean
}

const ImageMapPicker: React.FC<ImageMapPickerProps> = ({
  map_title,
  map_icon,
  list_title,
  list_icon,
  list_metadata,
  image_source,
  image_metadata,
  choices,
  minimum_selection: minSelection = 0,
  maximum_selection: maxSelection,
  disabled,
  sendAnswer,
  ready = true,
  // includeListView is only supported for WEB
  includeListView: includeListViewProp,
}) => {
  const theme = useTheme()
  const includeListView = isWeb() && includeListViewProp
  const [activeTab, setActiveTab] = useState(0)
  const [selectedRegions, setSelectedRegions] = useState<string[]>([])
  const [notices, setNotices] = useState<{ title: string; titleStyle: TextStyle }[]>([])

  const zoomRef = useRef<{ reset?: () => void } | null>(null)
  const regionsLength = selectedRegions.length
  const noMinSelection = minSelection === 0
  const noMaxSelection = maxSelection === undefined
  const isLimitReached = !noMaxSelection && regionsLength > (maxSelection ?? 0)
  const submitDisabled =
    disabled || isLimitReached || (!noMinSelection && regionsLength < minSelection)

  const choiceMap = useMemo(() => {
    const map: Record<string, ChoiceOption> = {}
    choices?.forEach((choice) => (map[choice.id] = choice))
    return map
  }, [choices])
  const groups = useMemo(
    () => denormalizeListMetadata(list_metadata, choiceMap),
    [list_metadata, choiceMap]
  )

  const maxReachedNotices = useMemo(
    () => [
      {
        title: I18n.t('ImageMap.notice.maxReached.title', {
          maxSelectionCount: maxSelection,
        }),
        titleStyle: getNoticeTitleStyle(theme),
      },
    ],
    [maxSelection, theme]
  )

  useEffect(() => {
    setNotices(isLimitReached ? maxReachedNotices : [])
  }, [isLimitReached, maxReachedNotices])

  const onChange = (incomingRegionIds: string[]) => {
    setSelectedRegions(incomingRegionIds)
  }

  const onListRegionPress = ({ id }: ChoiceOption) => {
    const nextRegions = [...selectedRegions]
    const indexOfRegion = nextRegions.indexOf(id)
    if (indexOfRegion === -1) {
      nextRegions.push(id)
    } else if (indexOfRegion !== -1) {
      nextRegions.splice(indexOfRegion, 1)
    }
    setSelectedRegions(nextRegions)
  }

  const setZoomRef = (node: { reset?: () => void } | null) => {
    if (node) {
      zoomRef.current = node
    }
  }

  const resetZoomScale = () => {
    if (zoomRef) {
      zoomRef.current?.reset?.()
    }
  }

  const onReset = () => {
    setSelectedRegions([])
    resetZoomScale()
  }

  const filterMetadata = (ids: string[], metadata: Record<string, Coordinates>) => {
    if (!Array.isArray(ids) || typeof metadata !== 'object') {
      return metadata
    }

    const filteredMetadata: Record<string, Coordinates> = {}
    ids.forEach((region) => (filteredMetadata[region] = metadata[region]))

    return filteredMetadata
  }

  const onNextPress = () => {
    if (!submitDisabled) {
      sendAnswer(
        choices.filter((choice) => selectedRegions.includes(choice.id)),
        filterMetadata(selectedRegions, image_metadata)
      )
    }
  }

  const imageMapProps = {
    zoom: true,
    zoomRef: setZoomRef,
    maximumZoomScale: Platform.OS === 'android' ? 2 : 8,
    // The web selector forwards this value to a DOM element, which requires a plain object.
    style: getImagePickerStyle(theme),
    imageScale: 0.95,
    imageSource: image_source,
    imageRegions: image_metadata,
    selectedRegions: selectedRegions,
    onChange: onChange,
  }

  return (
    <NoticeBarContainer
      top
      float={!includeListView}
      centerText
      containerStyle={getPickerContainerStyle(theme)}
      style={getNoticeStyle(theme)}
      localNotices={notices}
    >
      {includeListView ? (
        <>
          <Tabs value={activeTab} onChange={setActiveTab} analyticsName="ImageMap.TapBar">
            <TabsItem title={map_title} icon={map_icon} />
            <TabsItem title={list_title} icon={list_icon} testID="listTab" />
          </Tabs>
          <TabContent value={activeTab}>
            <TabContentItem index={0}>
              <ImageMapSelector {...imageMapProps} />
              <ImageMapHint maxSelectionCount={maxSelection} />
            </TabContentItem>
            <TabContentItem index={1}>
              <GroupedChoicePickerComponent
                multi
                groups={groups}
                style={getFooterSpacingStyle(theme)}
                selectedChoices={selectedRegions}
                onChoicePress={onListRegionPress}
                analyticsName="Intake.ImageMapPicker.List"
                testID="groupedChoicePicker"
              />
            </TabContentItem>
          </TabContent>
        </>
      ) : (
        <ImageMapSelector {...imageMapProps} />
      )}
      <ActionButtons onReset={onReset} onNextPress={onNextPress} submitDisabled={submitDisabled} />
      {!includeListView && <ImageMapHint maxSelectionCount={maxSelection} />}
      <ZoomHint modal={false} ready={ready} style={StyleSheet.absoluteFill} />
    </NoticeBarContainer>
  )
}

export default ImageMapPicker
